import { HttpHandlerFn, HttpRequest, HttpInterceptorFn, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthToken } from '@shared/models';
import { KeycloakService } from '@shared/services/api';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';

export const requestInterceptor: HttpInterceptorFn = (
	req: HttpRequest<unknown>,
	next: HttpHandlerFn
) => {
	const authService = inject(KeycloakService);
	const router = inject(Router);

	// Skip requests with 'exclude' header
	if (req.headers.get('exclude')) {
		return next(req);
	}

	// Apply credentials to the request
	const authReq = applyCredentials(req, authService);

	// State for token refresh flow
	const refreshTokenInProgress = false;
	const refreshTokenSubject = new BehaviorSubject<string | null>(null);

	return next(authReq).pipe(
		catchError((error) => {
			if (error.status === 401 && authService.getRefreshToken()) {
				return handleUnauthorizedError(
					authReq,
					next,
					authService,
					router,
					refreshTokenInProgress,
					refreshTokenSubject
				);
			}

			// Return all other errors
			return throwError(() => error.error);
		})
	);
};

function applyCredentials(
	request: HttpRequest<unknown>,
	authService: KeycloakService
): HttpRequest<unknown> {
	const token = authService.getAccessToken();
	const excludeUrl = /assets/gi;

	if (token && request.url.search(excludeUrl) === -1) {
		return request.clone({
			setHeaders: {
				Authorization: `Bearer ${token}`
			}
		});
	}
	return request;
}

function handleUnauthorizedError(
	request: HttpRequest<unknown>,
	next: HttpHandlerFn,
	authService: KeycloakService,
	router: Router,
	refreshTokenInProgress: boolean,
	refreshTokenSubject: BehaviorSubject<string | null>
): Observable<HttpEvent<unknown>> {
	// If refresh is already in progress
	if (refreshTokenInProgress) {
		return refreshTokenSubject.pipe(
			filter((token) => token !== null),
			take(1),
			switchMap((token) => {
				if (token) {
					return next(applyCredentials(request, authService));
				}
				refreshTokenInProgress = false;
				return throwError(() => new Error('Refresh token failed'));
			})
		);
	}

	// Start refresh token flow
	refreshTokenInProgress = true;
	refreshTokenSubject.next(null);
	authService.removeToken();

	return authService.refreshToken(authService.getRefreshToken() as string).pipe(
		switchMap((oauth: OAuthToken) => {
			const newToken = oauth.access_token;
			if (newToken) {
				refreshTokenSubject.next(newToken);
				return next(applyCredentials(request, authService));
			}
			return goToLogin(authService, router);
		}),
		catchError((e) => {
			goToLogin(authService, router);
			return throwError(() => e);
		}),
		finalize(() => {
			refreshTokenInProgress = false;
		})
	);
}

function goToLogin(authService: KeycloakService, router: Router): Observable<never> {
	authService.clearToken();
	router.navigate(['/auth'], {
		queryParams: {
			page: router.url.substring(1)
		}
	});
	return throwError(() => new Error('Authentication required'));
}
